/**
 * Modèles de checklist du rapport d'intervention (Q23) : une seule définition
 * pour l'API (bornes serveur, API2-41) et l'écran de réglages. Un modèle ne se
 * modifie jamais en place : les rapports remplis citent leur version ;
 * modifier crée une nouvelle version et désactive l'ancienne.
 */
import { z } from 'zod';
import { INTERVENTION_TYPES } from './domain.js';

/** Points par modèle et modèles actifs par entreprise. */
export const CHECKLIST_TEMPLATE_ITEMS = 100;
export const CHECKLIST_TEMPLATES_MAX = 100;
/** Photos exigées par point « photo » : pas plus qu'un emplacement de photos du rapport. */
export const CHECKLIST_PHOTOS_MAX = 30;

export const CHECKLIST_ITEM_KINDS = ['bool', 'number', 'text', 'photo'] as const;
export type ChecklistItemKind = (typeof CHECKLIST_ITEM_KINDS)[number];

const photoCount = z.number().int().min(0).max(CHECKLIST_PHOTOS_MAX);

export const checklistItem = z.object({
  key: z.string().trim().min(1).max(80),
  label: z.string().trim().min(1).max(200),
  kind: z.enum(CHECKLIST_ITEM_KINDS),
  required: z.boolean().default(false),
  unit: z.string().trim().max(20).optional(),
  // Point « photo » : clichés avant/après exigés pour qu'il se coche seul.
  requiredMedia: z.object({ before: photoCount, after: photoCount }).strict().optional(),
});
export type ChecklistItem = z.output<typeof checklistItem>;

export const checklistTemplateBody = z
  .object({
    name: z.string().trim().min(1).max(120),
    interventionType: z.enum(INTERVENTION_TYPES).optional(),
    equipmentCategory: z.string().trim().max(100).optional(),
    items: z
      .array(checklistItem)
      .min(1)
      .max(CHECKLIST_TEMPLATE_ITEMS)
      .refine((items) => new Set(items.map((item) => item.key)).size === items.length, {
        error: 'Chaque point de la checklist a une clé différente.',
      }),
  })
  .strict();
export type ChecklistTemplateBody = z.input<typeof checklistTemplateBody>;

/** Modèle tel que l'API le rend (`GET /checklist-templates`). */
export interface ChecklistTemplate {
  id: string;
  name: string;
  intervention_type: (typeof INTERVENTION_TYPES)[number] | null;
  equipment_category: string | null;
  items: ChecklistItem[];
  active: boolean;
  created_at: string;
}
